import { available, isU64, unavailable } from './integer.js';
import type { OutcomePoolQuoteContext } from './outcome-pool.js';
import {
  type OutcomeSnapshotIdentity,
  type ResolvedCheckedOutcomeSnapshot,
  resolveCheckedOutcomeSnapshot,
} from './snapshot-resolver.js';
import type { CreatorFeeOn, MarketLifecycle, PoolMathResult, TokenIndex } from './types.js';

export interface BondingCurvePoolQuoteSnapshot {
  virtualQuoteReserveD6: string;
  poolQuoteReserveD6: string;
  initialOutcomeSupplyD6: string;
  poolOutcomeReserveD6: string;
  closedForTrade: boolean;
}

export interface BondingCurveTradeGateQuoteSnapshot {
  outcomeConvictionPpb: readonly string[];
  lastConvictionUpdateUnixSeconds: string;
  convictionBaselineReserveQuoteD6: string;
  convictionRetentionPerSecondPpb: string;
  convictionResolutionThresholdPpb: string;
  convictionReferenceMarginPpb: string;
  /** Exact first contract threshold touch from the indexed conviction anchor. */
  resolutionAtUnixSeconds?: string | null;
  timedTradingWindow?: { startUnixSeconds: string; endUnixSeconds: string };
  tradingStartedUnixSeconds?: string;
  resolutionThresholdFloorMarginPpb?: string;
  resolutionThresholdDecayDelaySecs?: string;
  resolutionThresholdDropIntervalSecs?: string;
  resolutionThresholdDropPpb?: string;
}

export interface CpSwapPoolQuoteSnapshot {
  quoteReserveD6: string;
  outcomeReserveD6: string;
  tradeFeeRateMillionths: string;
  creatorFeeRateMillionths: string;
  creatorFeeEnabled: boolean;
  creatorFeeOn: CreatorFeeOn;
  outcomeTokenIndex: TokenIndex;
  swapEnabled: boolean;
  openTimeUnixSeconds: string;
}

export interface OutcomePoolQuoteSnapshot extends OutcomeSnapshotIdentity {
  realQuoteReserveD6: string | null;
  bondingCurve: BondingCurvePoolQuoteSnapshot | null;
  cpSwap: CpSwapPoolQuoteSnapshot | null;
}

export interface MarketPoolQuoteSnapshot {
  market: string;
  chainSlot: string;
  chainUnixTimestampSeconds: string;
  lifecycle: MarketLifecycle;
  winningOutcomeIndex: number | null;
  bondingCurvesClosed: boolean | null;
  buyFeeBps: number | null;
  sellBaseFeeBps: number | null;
  sellPenaltyFeeBps: number | null;
  prizePoolFeeBps: number | null;
  minimumQuoteTradeD6: string | null;
  bondingCurveTradeGate: BondingCurveTradeGateQuoteSnapshot | null;
  outcomes: readonly OutcomePoolQuoteSnapshot[];
}

export interface CheckedOutcomePoolQuoteContext
  extends ResolvedCheckedOutcomeSnapshot<OutcomePoolQuoteSnapshot> {
  context: OutcomePoolQuoteContext;
}

export function checkedOutcomePoolQuoteContextFromSnapshot(
  snapshot: MarketPoolQuoteSnapshot,
  expectedMarket: string,
  expectedOutcomeIndex: number,
  expectedOutcomeMint: string,
  stateStale: boolean = false,
): PoolMathResult<CheckedOutcomePoolQuoteContext> {
  const resolved = resolveCheckedOutcomeSnapshot({
    snapshot,
    expectedMarket,
    expectedOutcomeIndex,
    expectedOutcomeMint,
  });
  if (!resolved.available) return resolved;
  const context = hydrateContext(snapshot, resolved.value.outcome, stateStale);
  if (!context.available) return context;
  return available({ ...resolved.value, context: context.value });
}

function hydrateContext(
  snapshot: MarketPoolQuoteSnapshot,
  outcome: OutcomePoolQuoteSnapshot,
  stateStale: boolean,
): PoolMathResult<OutcomePoolQuoteContext> {
  const slot = unsignedWire(snapshot.chainSlot);
  const unixTimestampSeconds = unsignedWire(snapshot.chainUnixTimestampSeconds);
  if (slot === null || unixTimestampSeconds === null) return unavailable('invalid_amount');
  const chainClock = { slot, unixTimestampSeconds };
  const venue = {
    lifecycle: snapshot.lifecycle,
    outcomeIndex: outcome.outcomeIndex,
    winningOutcomeIndex: snapshot.winningOutcomeIndex,
    bondingCurveClosed:
      snapshot.bondingCurvesClosed === true || (outcome.bondingCurve?.closedForTrade ?? false),
    hasCpSwapPool: outcome.cpSwap !== null,
  } as const;
  if (stateStale) return available({ chainClock, poolModel: 'stale', venue });
  if (snapshot.lifecycle === 'trading') {
    return bondingCurveContext(snapshot, outcome, venue, chainClock);
  }
  if (snapshot.lifecycle === 'graduated' && outcome.cpSwap !== null) {
    const quoteReserveD6 = unsignedWire(outcome.cpSwap.quoteReserveD6);
    const outcomeReserveD6 = unsignedWire(outcome.cpSwap.outcomeReserveD6);
    const tradeFeeRateMillionths = unsignedWire(outcome.cpSwap.tradeFeeRateMillionths);
    const creatorFeeRateMillionths = unsignedWire(outcome.cpSwap.creatorFeeRateMillionths);
    const openTimeUnixSeconds = unsignedWire(outcome.cpSwap.openTimeUnixSeconds);
    if (
      quoteReserveD6 === null ||
      outcomeReserveD6 === null ||
      tradeFeeRateMillionths === null ||
      creatorFeeRateMillionths === null ||
      openTimeUnixSeconds === null
    ) {
      return unavailable('invalid_amount');
    }
    return available({
      chainClock,
      poolModel: 'cp_swap',
      venue,
      cpSwap: {
        quoteReserveD6,
        outcomeReserveD6,
        tradeFeeRateMillionths,
        creatorFeeRateMillionths,
        creatorFeeEnabled: outcome.cpSwap.creatorFeeEnabled,
        creatorFeeOn: outcome.cpSwap.creatorFeeOn,
        outcomeTokenIndex: outcome.cpSwap.outcomeTokenIndex,
        swapEnabled: outcome.cpSwap.swapEnabled,
        openTimeUnixSeconds,
      },
    });
  }
  return available({ chainClock, poolModel: 'missing', venue });
}

function bondingCurveContext(
  snapshot: MarketPoolQuoteSnapshot,
  outcome: OutcomePoolQuoteSnapshot,
  venue: OutcomePoolQuoteContext['venue'],
  chainClock: OutcomePoolQuoteContext['chainClock'],
): PoolMathResult<OutcomePoolQuoteContext> {
  const curve = outcome.bondingCurve;
  const gate = snapshot.bondingCurveTradeGate;
  if (
    curve === null ||
    gate === null ||
    snapshot.buyFeeBps === null ||
    snapshot.sellBaseFeeBps === null ||
    snapshot.sellPenaltyFeeBps === null ||
    snapshot.prizePoolFeeBps === null ||
    snapshot.minimumQuoteTradeD6 === null
  ) {
    return available({ chainClock, poolModel: 'missing', venue });
  }
  const virtualQuoteReserveD6 = unsignedWire(curve.virtualQuoteReserveD6);
  const poolQuoteReserveD6 = unsignedWire(curve.poolQuoteReserveD6);
  const initialOutcomeSupplyD6 = unsignedWire(curve.initialOutcomeSupplyD6);
  const poolOutcomeReserveD6 = unsignedWire(curve.poolOutcomeReserveD6);
  const minimumQuoteTradeD6 = unsignedWire(snapshot.minimumQuoteTradeD6);
  const lastConvictionUpdateUnixSeconds = unsignedWire(gate.lastConvictionUpdateUnixSeconds);
  const convictionBaselineReserveQuoteD6 = unsignedWire(gate.convictionBaselineReserveQuoteD6);
  const convictionRetentionPerSecondPpb = unsignedWire(gate.convictionRetentionPerSecondPpb);
  const convictionResolutionThresholdPpb = unsignedWire(gate.convictionResolutionThresholdPpb);
  const convictionReferenceMarginPpb = unsignedWire(gate.convictionReferenceMarginPpb);
  const resolutionAtUnixSeconds =
    gate.resolutionAtUnixSeconds == null
      ? gate.resolutionAtUnixSeconds
      : unsignedWire(gate.resolutionAtUnixSeconds);
  const timedTradingWindow = gate.timedTradingWindow;
  const timedStart =
    timedTradingWindow === undefined
      ? undefined
      : (unsignedWire(timedTradingWindow.startUnixSeconds) ?? undefined);
  const timedEnd =
    timedTradingWindow === undefined
      ? undefined
      : (unsignedWire(timedTradingWindow.endUnixSeconds) ?? undefined);
  if (
    virtualQuoteReserveD6 === null ||
    poolQuoteReserveD6 === null ||
    initialOutcomeSupplyD6 === null ||
    poolOutcomeReserveD6 === null ||
    minimumQuoteTradeD6 === null ||
    lastConvictionUpdateUnixSeconds === null ||
    convictionBaselineReserveQuoteD6 === null ||
    convictionRetentionPerSecondPpb === null ||
    convictionResolutionThresholdPpb === null ||
    (gate.resolutionAtUnixSeconds !== undefined &&
      gate.resolutionAtUnixSeconds !== null &&
      resolutionAtUnixSeconds === null) ||
    (timedTradingWindow !== undefined && (timedStart === undefined || timedEnd === undefined)) ||
    convictionReferenceMarginPpb === null
  ) {
    return unavailable('invalid_amount');
  }
  const buyFeeBps = unsignedNumber(snapshot.buyFeeBps);
  const sellBaseFeeBps = unsignedNumber(snapshot.sellBaseFeeBps);
  const sellPenaltyFeeBps = unsignedNumber(snapshot.sellPenaltyFeeBps);
  const prizePoolFeeBps = unsignedNumber(snapshot.prizePoolFeeBps);
  if (
    buyFeeBps === null ||
    sellBaseFeeBps === null ||
    sellPenaltyFeeBps === null ||
    prizePoolFeeBps === null
  ) {
    return unavailable('invalid_amount');
  }
  const realQuoteReservesD6: bigint[] = [];
  for (const candidate of snapshot.outcomes) {
    if (candidate.realQuoteReserveD6 === null) return unavailable('invalid_amount');
    const reserveD6 = unsignedWire(candidate.realQuoteReserveD6);
    if (reserveD6 === null) return unavailable('invalid_amount');
    realQuoteReservesD6.push(reserveD6);
  }
  const outcomeConvictionPpb: bigint[] = [];
  for (const convictionPpb of gate.outcomeConvictionPpb) {
    const parsed = unsignedWire(convictionPpb);
    if (parsed === null) return unavailable('invalid_amount');
    outcomeConvictionPpb.push(parsed);
  }
  return available({
    chainClock,
    poolModel: 'bonding_curve',
    venue,
    bondingCurve: {
      state: {
        virtualQuoteReserveD6,
        poolQuoteReserveD6,
        initialOutcomeSupplyD6,
        poolOutcomeReserveD6,
      },
      fees: {
        buyFeeBps,
        sellBaseFeeBps,
        sellPenaltyFeeBps,
        prizePoolFeeBps,
        minimumQuoteTradeD6,
      },
      convictionReferenceMarginPpb,
      convictionOutcomeIndices: snapshot.outcomes.map((candidate) => candidate.outcomeIndex),
      conviction: {
        realQuoteReservesD6,
        outcomeConvictionPpb,
        lastConvictionUpdateUnixSeconds,
        convictionBaselineReserveQuoteD6,
        convictionRetentionPerSecondPpb,
        convictionResolutionThresholdPpb,
        resolutionAtUnixSeconds,
        timedTradingWindow:
          timedStart === undefined || timedEnd === undefined
            ? undefined
            : { startUnixSeconds: timedStart, endUnixSeconds: timedEnd },
      },
    },
  });
}

function unsignedWire(value: string): bigint | null {
  if (!/^(?:0|[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isU64(parsed) ? parsed : null;
}

function unsignedNumber(value: number): bigint | null {
  if (!Number.isSafeInteger(value) || value < 0) return null;
  return BigInt(value);
}
